/**
 * Voice Mate SDK Error Classes
 * Provides structured error handling with error codes and details
 */

/**
 * Base error class for all Voice Mate SDK errors
 */
export class VoiceMateError extends Error {
  constructor(message, code, details = {}) {
    super(message);
    this.name = 'VoiceMateError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      details: this.details,
      timestamp: this.timestamp
    };
  }
}

/**
 * Connection-related errors (WebSocket, network)
 */
export class ConnectionError extends VoiceMateError {
  constructor(message, details) {
    super(message, 'CONNECTION_ERROR', details);
    this.name = 'ConnectionError';
  }
}

/**
 * Session management errors (session creation, termination)
 */
export class SessionError extends VoiceMateError {
  constructor(message, details) {
    super(message, 'SESSION_ERROR', details);
    this.name = 'SessionError';
  }
}

/**
 * Audio-related errors (microphone access, playback)
 */
export class AudioError extends VoiceMateError {
  constructor(message, details) {
    super(message, 'AUDIO_ERROR', details);
    this.name = 'AudioError';
  }
}

/**
 * Configuration validation errors
 */
export class ValidationError extends VoiceMateError {
  constructor(message, details) {
    super(message, 'VALIDATION_ERROR', details);
    this.name = 'ValidationError';
  }
}

/**
 * SDK/Server version compatibility errors
 */
export class CompatibilityError extends VoiceMateError {
  constructor(message, details) {
    super(message, 'COMPATIBILITY_ERROR', details);
    this.name = 'CompatibilityError';
  }
}

/**
 * Error codes for common scenarios
 */
export const ERROR_CODES = {
  // Connection errors (1xxx)
  WEBSOCKET_FAILED: 1001,
  CONNECTION_TIMEOUT: 1002,
  RECONNECT_FAILED: 1003,

  // Session errors (2xxx)
  SESSION_CREATE_FAILED: 2001,
  SESSION_NOT_FOUND: 2002,
  INVALID_BOT_NUMBER: 2003,
  SERVER_UNAVAILABLE: 2004,

  // Audio errors (3xxx)
  MIC_PERMISSION_DENIED: 3001,
  MIC_NOT_FOUND: 3002,
  AUDIO_CONTEXT_FAILED: 3003,
  PLAYBACK_FAILED: 3004,

  // Validation errors (4xxx)
  INVALID_CONFIG: 4001,
  MISSING_REQUIRED_PARAM: 4002,

  // Compatibility errors (5xxx)
  VERSION_MISMATCH: 5001,
  UNSUPPORTED_BROWSER: 5002
};
